import React, { useEffect, useMemo, useState, useRef } from 'react'
import { ReactComponent as BigArrow } from '../../../assets/svg/BigArrow.svg'
import Loading from '../../Loading'
import { loadClassCheckingData, loadCounsellorCheckingData, loadOrganizationCheckingData, loadStudentCheckingData } from '../../../server/server'
import { getPercentage, useGetAnalyticTypeFromPath, useHasStudentInPath, useQuery } from '../../../utils/util'
import { useTranslation } from 'react-i18next'
import DonutChartStudentCheckIn from './DonutChartStudentCheckIn'
import InfoButton from '../../common/InfoButton'
import { calculateDateRange } from '../../../utils/dateUtils'
import StudentsIncludedSummary from '../StudentsIncludedSummary'
import AnalyticsLoadingOverlay from '../AnalyticsLoadingOverlay'
import CheckInSkeleton from '../skeleton/CheckInSkeleton'

function CheckInWidget({ daysFilter, selectedUsersScope, isCounsellor = false, analyticsType = 'student', scopeLabel }) {
    const { t } = useTranslation()
    const isStudentAnalytics = useHasStudentInPath()
    const query = useQuery()
    const currentId = query.get('id')
    const requestVersion = useRef(0)
    const [loading, setLoading] = useState(true)
    const [initLoading, setInitLoading] = useState(true)
    const [data, setData] = useState(null)
    const [loadedKey, setLoadedKey] = useState(null)
    const filterKey = JSON.stringify([analyticsType, currentId, selectedUsersScope, daysFilter])
    const summaryLoading = loading || loadedKey !== filterKey
    const yearLoading = analyticsType === 'year' && summaryLoading
    const showStudentSummary = ['year', 'counsellor', 'organization'].includes(analyticsType)

    const showLoading = useMemo(() => loading || !data || !data.checkIns, [loading, data])

    useEffect(() => {
        setLoading(true)
        loadData()
        return () => { requestVersion.current += 1 }
    }, [daysFilter, currentId, selectedUsersScope, analyticsType])

    const loadData = async () => {
        const version = ++requestVersion.current
        let callFunction
        switch (analyticsType) {
            case 'student':
                callFunction = loadStudentCheckingData
                break
            case 'class':
                callFunction = loadClassCheckingData
                break
            case 'year':
            case 'counsellor':
                callFunction = loadCounsellorCheckingData
                break
            case 'organization':
                callFunction = loadOrganizationCheckingData
                break
            default:
                callFunction = loadStudentCheckingData
        }

        const { startDate, endDate } = calculateDateRange(daysFilter)

        const resp = await callFunction(isCounsellor ? selectedUsersScope : currentId, startDate, endDate).catch(() => ({ status: 500 }))
        if (version !== requestVersion.current) return
        if (resp.status === 200) {
            setData(resp.data)
        } else {
            setData(null)
        }
        setLoading(false)
        setInitLoading(false)
        setLoadedKey(filterKey)
    }

    const percentage = useMemo(() => {
        if (!data) return 0
        return data?.checkIns / data?.averageClassCheckIns
    }, [data])

    const different = useMemo(() => {
        if (percentage === 0) return 0
        return percentage > 0 ? Math.round(100 * (percentage - 1)) : percentage < 0 ? Math.round(100 * (1 - percentage)) : 0
    }, [percentage])

    return (
        <>
            {showStudentSummary && <StudentsIncludedSummary data={data} loading={summaryLoading} daysFilter={daysFilter} scope={selectedUsersScope} scopeLabel={scopeLabel} />}
            <div
                className="relative"
                role={analyticsType === 'year' ? 'region' : undefined}
                aria-label={analyticsType === 'year' ? t('Check-ins overview for selected classes') : undefined}
                aria-busy={analyticsType === 'year' ? yearLoading : undefined}
            >
                <DataContainer data={data} different={different} percentage={different / 100} analyticsData={data} blur={showLoading && !yearLoading} daysFilter={daysFilter} type={analyticsType} loading={yearLoading} />
                {yearLoading ? <AnalyticsLoadingOverlay /> : showLoading && <LoadingContainer showEmptyData={!loading && !data?.checkIns} />}
            </div>
        </>
    )
}

export default CheckInWidget

const DataContainer = ({ different, percentage, analyticsData, blur, daysFilter, data, type, loading = false }) => {
    const { t } = useTranslation()
    const isStudentAnalytics = useHasStudentInPath()
    const analyticType = useGetAnalyticTypeFromPath()
    const total_emotions = data?.checkIns
    const comfortableEmotions = data?.comfortableEmotions ?? 0
    const comfortablePercentage = getPercentage(comfortableEmotions, total_emotions)
    const uncomfortablePercentage = getPercentage(data?.uncomfortableEmotions, total_emotions)

    const previousTotalEmotions = data?.previousPeriod?.checkIns ?? 0

    const trendCheckIns = previousTotalEmotions === 0 ? 100 : getPercentage(total_emotions, previousTotalEmotions) - 100
    const positiveTrendCheckIns = trendCheckIns > 0
    const sameTrend = trendCheckIns === 0

    const trendCheckInsLabel = useMemo(() => {
        if (sameTrend) return t('Same number of check-ins recorded during this period compared to the previous one')
        else if (positiveTrendCheckIns) return t('more check-ins recorded during this period compared to the previous one')
        else return t('fewer check-ins recorded during this period compared to the previous one')
    }, [trendCheckIns, sameTrend, positiveTrendCheckIns, t])

    const previousComfortableEmotions = data?.previousPeriod?.comfortableEmotions ?? 0

    const trendComfortableEmotions = previousComfortableEmotions === 0 ? 100 : getPercentage(comfortableEmotions, previousComfortableEmotions) - 100
    const positiveTrendComfortableEmotions = trendComfortableEmotions > 0
    const sameTrendComfortableEmotions = trendComfortableEmotions === 0

    const trendComfortableEmotionsLabel = useMemo(() => {
        if (sameTrendComfortableEmotions) return t('Same number of comfortable emotions logged during this period compared to the previous one.')
        else if (positiveTrendComfortableEmotions) return t('More comfortable emotions logged during this period compared to the previous one.')
        else return t('fewer comfortable emotions logged during this period compared to the previous one.')
    }, [trendComfortableEmotions, sameTrendComfortableEmotions, positiveTrendComfortableEmotions, t])

    const getTitleByAnalyticType = (analyticType) => {
        const titles = {
            student: t('Student check-ins overview'),
            class: t('Class check-ins overview'),
            year: t('Check-ins overview for selected classes'),
            counsellor: t('Check-ins overview for selected student groups'),
            organization: t('Check-ins overview for selected student groups'),
        }
        return titles[analyticType] || t('Check-ins')
    }

    const getInfoByAnalyticType = (analyticType) => {
        const titles = {
            student: t(
                'This section provides an overview of the student’s check-ins for the selected period. It shows the total number of check-ins, the percentage breakdown of comfortable and uncomfortable emotions, and highlights changes compared to the previous period.'
            ),
            class: t('Shows the class’s emotional check-ins for the selected period, with a breakdown of comfortable and uncomfortable emotions.'),
            year: t('View check-in and emotion trends over time to spot changes in student engagement'),
            counsellor: t('View check-in and emotion trends over time to spot changes in student engagement'),
            organization: t('View check-in and emotion trends over time to spot changes in student engagement'),
        }
        return titles[analyticType] || t('Demo text')
    }

    return (
        <div className={`bg-[#FFF] flex flex-col gap-6 p-6 rounded-[24px] mt-4 ${blur ? 'blur' : ''}`} data-tour="checkins">
            <div className="flex flex-row items-center justify-between gap-5 flex-1">
                <div className="flex flex-row items-center gap-2">
                    <p className="text-[18px] leading-[25px] font-medium text-darkenPurple ">{getTitleByAnalyticType(type)}</p>
                    <div className="px-[10px] py-[3px] rounded-full bg-[#413F5E14]">
                        <p className="text-[13px] leading-[18px] text-[#222222]">{t(daysFilter?.label)}</p>
                    </div>
                </div>
                <button>
                    <InfoButton text={getInfoByAnalyticType(type)} />
                </button>
            </div>
            {!loading && Number.isInteger(data?.studentsIncluded) && Number.isInteger(data?.studentsCheckedIn) && (
                <p className="text-[14px] text-strongGray -mt-4">{t('Based on {{checkIns}} check-ins from {{checkedIn}} of {{included}} included students', {
                    checkIns: data.checkIns, checkedIn: data.studentsCheckedIn, included: data.studentsIncluded,
                })}</p>
            )}
            {loading ? <CheckInSkeleton /> : (
                <>
                    <div className="flex flex-row gap-6">
                        <div className="flex flex-row items-center gap-2">
                            <p className="text-[16px] text-[#2B793F] leading-5 font-semibold">{comfortablePercentage}%</p>
                            <DonutChartStudentCheckIn percentage={uncomfortablePercentage ?? 0} total={total_emotions} />
                            <p className="text-[16px] text-[#78020B] leading-5 font-semibold">{uncomfortablePercentage}%</p>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-row items-start gap-[6px]">
                                <div className={`${!positiveTrendCheckIns ? 'rotate-0' : 'rotate-180'} transition`}>
                                    <BigArrow color={`${!positiveTrendCheckIns ? '#FC4C59' : '#6FCC87'}`} opacity={sameTrend ? 0 : 1} />
                                </div>
                                <p className="text-[18px] leading-[24px] font-semibold text-[#77768D] ">
                                    {!sameTrend && <span className={`${!positiveTrendCheckIns ? 'text-[#FC4C59]' : 'text-[#2B793F]'}`}>{trendCheckIns}% </span>}
                                    {trendCheckInsLabel}
                                </p>
                            </div>
                            <div className="flex flex-row items-start gap-[6px]">
                                <div className={`${!positiveTrendComfortableEmotions ? 'rotate-0' : 'rotate-180'} transition`}>
                                    <BigArrow color={`${!positiveTrendComfortableEmotions ? '#FC4C59' : '#6FCC87'}`} opacity={sameTrendComfortableEmotions ? 0 : 1} />
                                </div>
                                <p className="text-[18px] leading-[24px] font-semibold text-[#77768D] ">
                                    {!sameTrendComfortableEmotions && (
                                        <span className={`${!positiveTrendComfortableEmotions ? 'text-[#FC4C59]' : 'text-[#2B793F]'}`}>{trendComfortableEmotions}% </span>
                                    )}
                                    {trendComfortableEmotionsLabel}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-8">
                        <div className="flex flex-row items-center gap-2 ">
                            <div className="w-8 h-4 border-[2px] border-[#2B793F] bg-[#6FCC8799] rounded-[4px]"> </div>
                            <p className="text-[16px] leading-[20px] font-semibold text-[#77768D] ">{t('Comfortable emotions')}</p>
                        </div>
                        <div className="flex flex-row items-center gap-2 ">
                            <div className="w-8 h-4 border-[2px] border-[#78020B] bg-[#FC4C5999] rounded-[4px]"> </div>
                            <p className="text-[16px] leading-[20px] font-semibold text-[#77768D] ">{t('Uncomfortable emotions')}</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export const LoadingContainer = ({ showEmptyData = false }) => {
    const { t } = useTranslation()
    return (
        <div className="absolute h-full w-full top-0 z-1">
            <div className="h-full blur"></div>
            {showEmptyData ? (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center ">
                    <div className="flex flex-col items-center">
                        <p className="text-[16px] leading-[25px] font-bold text-mudPurple ">{t('No data found')}</p>
                        <p className="text-[14px] leading-[25px] font-medium text-mudPurple text-center">
                            {t('This element will be updated once enough app-activity has been recorded')}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-1">
                    <Loading size={50} stroke={5} secondaryColor="white" primaryColor="#7C82C3" />
                </div>
            )}
        </div>
    )
}
